import {
  Alert,
  App,
  Avatar,
  Button,
  Card,
  Col,
  DatePicker,
  Dropdown,
  Empty,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Skeleton,
  Space,
  Statistic,
  Switch,
  Tag,
  Tooltip,
  Typography,
  theme as antdTheme,
} from 'antd';
import {
  CopyOutlined,
  DollarOutlined,
  FileTextOutlined,
  HistoryOutlined,
  PhoneOutlined,
  PrinterOutlined,
  UserOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import dayjs, { type Dayjs } from 'dayjs';
import {
  feeApi,
  type CollectInput,
  type Invoice,
  type PaymentMethod,
  type Receipt,
  type StudentLedger,
} from '@/api/fee';
import { financeApi } from '@/api/finance';
import { studentsApi } from '@/api/students';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { AuditHistoryDrawer } from '@/shared/components/ui';
import { downloadFile } from '@/shared/lib/download';
import { fileUrl } from '@/shared/lib/file-url';
import { extractError } from '@/shared/lib/error-message';

const PKR = (n: number | string) =>
  Number(n).toLocaleString('en-PK', { maximumFractionDigits: 2 });

const METHOD_LABEL: Record<PaymentMethod, string> = {
  CASH: 'Cash',
  BANK_TRANSFER: 'Bank transfer',
  ONLINE_TRANSFER: 'Online transfer',
};

// Oldest-first: ONE_TIME (no periodMonth) first, then by periodMonth asc, then by id.
function sortOldestFirst(invs: Invoice[]): Invoice[] {
  return [...invs].sort((a, b) => {
    if (!a.periodMonth && b.periodMonth) return -1;
    if (a.periodMonth && !b.periodMonth) return 1;
    if (a.periodMonth && b.periodMonth) {
      const c = a.periodMonth.localeCompare(b.periodMonth);
      if (c !== 0) return c;
    }
    return a.id - b.id;
  });
}

function fifoAllocate(amount: number, invs: Invoice[]): Record<number, number> {
  let remaining = Math.max(0, amount);
  const out: Record<number, number> = {};
  for (const i of sortOldestFirst(invs)) {
    const due = Number(i.dueAmount);
    if (remaining <= 0) {
      out[i.id] = 0;
      continue;
    }
    const take = Math.min(due, remaining);
    out[i.id] = Math.round(take * 100) / 100;
    remaining -= take;
  }
  return out;
}

function groupByMonth(invs: Invoice[]): { key: string; label: string; invoices: Invoice[]; total: number }[] {
  const groups = new Map<string, Invoice[]>();
  for (const i of invs) {
    const key = i.periodMonth ?? '__one_time__';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(i);
  }
  return [...groups.entries()]
    .sort((a, b) => {
      if (a[0] === '__one_time__') return -1;
      if (b[0] === '__one_time__') return 1;
      return a[0].localeCompare(b[0]);
    })
    .map(([key, invoices]) => ({
      key,
      label: key === '__one_time__' ? 'One-time charges' : dayjs(key).format('MMM YYYY'),
      invoices,
      total: invoices.reduce((s, i) => s + Number(i.dueAmount), 0),
    }));
}

export function FeeCashierPage() {
  const params = useParams<{ enrollmentId: string }>();
  const enrollmentId = Number(params.enrollmentId);
  const navigate = useNavigate();

  const ledgerQuery = useQuery({
    queryKey: ['fee', 'ledger', enrollmentId],
    queryFn: () => feeApi.ledger(enrollmentId),
    enabled: Number.isFinite(enrollmentId),
  });

  if (ledgerQuery.isLoading) return <Skeleton active paragraph={{ rows: 10 }} />;
  if (ledgerQuery.isError || !ledgerQuery.data) {
    return (
      <Alert
        type="error"
        showIcon
        title="Could not load student"
        description={extractError(ledgerQuery.error)}
        action={<Button onClick={() => navigate('/fee/collect')}>Back</Button>}
      />
    );
  }
  return (
    <Cashier
      ledger={ledgerQuery.data}
      onRefresh={() => ledgerQuery.refetch()}
      updatedAt={ledgerQuery.dataUpdatedAt}
    />
  );
}

interface CollectFormShape {
  paymentDate: Dayjs;
  paymentMethod: PaymentMethod;
  bankAccountId?: number;
  proofReference?: string;
  amountReceived: number;
}

function Cashier({
  ledger,
  onRefresh,
  updatedAt,
}: {
  ledger: StudentLedger;
  onRefresh: () => void;
  updatedAt: number;
}) {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const qc = useQueryClient();
  const { token } = antdTheme.useToken();
  const canCreate = useAuthStore((s) => s.hasAuthority('FEE_MGMT:CREATE'));
  const [form] = Form.useForm<CollectFormShape>();

  const openInvoices = sortOldestFirst(
    ledger.invoices.filter((i) => !i.reversed && Number(i.dueAmount) > 0),
  );
  const totalOutstanding = openInvoices.reduce((s, i) => s + Number(i.dueAmount), 0);
  const groups = groupByMonth(openInvoices);
  const advanceBalance = Number(ledger.advanceBalance || 0);

  const studentQuery = useQuery({
    queryKey: ['student', ledger.studentId, 'cashier'],
    queryFn: () => studentsApi.get(ledger.studentId),
    enabled: !!ledger.studentId,
    staleTime: 5 * 60 * 1000,
  });
  const student = studentQuery.data;
  const primaryGuardian = student?.guardians?.find((g) => g.primaryContact) ?? student?.guardians?.[0];

  const banksQuery = useQuery({
    queryKey: ['finance', 'bank-accounts', 'active'],
    queryFn: () => financeApi.listBankAccounts(),
    staleTime: 5 * 60 * 1000,
  });
  const banks = (banksQuery.data ?? []).filter((b) => b.active);
  const noBanks = !banksQuery.isLoading && banks.length === 0;

  // The schema guarantees at most one cash account (unique partial index on
  // is_cash). For BANK_TRANSFER / ONLINE_TRANSFER we remember the cashier's last
  // pick per method so the second receipt of the day doesn't need a click.
  const cashDrawer = banks.find((b) => b.cash);
  const lastBankKey = (m: PaymentMethod) => `school.cashier.lastBank.${m}`;
  const pickDefaultBank = (m: PaymentMethod): number | undefined => {
    if (m === 'CASH') return cashDrawer?.id;
    const stored = Number(localStorage.getItem(lastBankKey(m)));
    if (stored && banks.some((b) => !b.cash && b.id === stored)) return stored;
    return banks.find((b) => !b.cash)?.id;
  };

  const [justCreated, setJustCreated] = useState<Receipt | null>(null);
  const [customMode, setCustomMode] = useState(false);
  const [touchedRows, setTouchedRows] = useState<Set<number>>(new Set());
  const [historyReceiptId, setHistoryReceiptId] = useState<number | null>(null);
  const canViewHistory = useAuthStore(
    (s) => s.hasAuthority('ROLE_SUPER_ADMIN') || s.hasAuthority('ROLE_PRINCIPAL'),
  );
  const amountRef = useRef<HTMLInputElement | null>(null);

  // Esc returns to the search list — cashiers blast through students, so a quick
  // keyboard exit beats reaching for "Back to search". Bails when an AntD overlay
  // (modal/drawer/select/picker) is open so we don't steal its Esc.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const overlayOpen =
        document.querySelector('.ant-modal-mask:not(.ant-modal-mask-hidden)') ||
        document.querySelector('.ant-drawer-mask:not([aria-hidden="true"])') ||
        document.querySelector('.ant-select-dropdown:not(.ant-select-dropdown-hidden)') ||
        document.querySelector('.ant-picker-dropdown:not(.ant-picker-dropdown-hidden)');
      if (overlayOpen) return;
      navigate('/fee/collect');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);
  // `paying` lives outside the form so we don't fight AntD's Form.Item value-control
  // machinery. The form only owns scalar fields (amountReceived / method / bank / proof).
  const [paying, setPaying] = useState<Record<number, number>>(() =>
    Object.fromEntries(openInvoices.map((i) => [i.id, 0])),
  );

  const collect = useMutation({
    mutationFn: (body: CollectInput) => feeApi.collect(body),
    onSuccess: (receipt) => {
      message.success(`Receipt #${receipt.receiptNumber} created · PKR ${PKR(receipt.totalAmount)}`);
      qc.invalidateQueries({ queryKey: ['fee', 'ledger', ledger.enrollmentId] });
      setJustCreated(receipt);
      // Reset state for next transaction; the success card above carries the Print button.
      form.resetFields(['amountReceived', 'proofReference']);
      setPaying(Object.fromEntries(openInvoices.map((i) => [i.id, 0])));
      setTouchedRows(new Set());
      // Put focus back where the next cashier action begins.
      setTimeout(() => amountRef.current?.focus(), 0);
    },
    onError: (e) => message.error(extractError(e)),
  });

  const watchedMethod = Form.useWatch('paymentMethod', form);
  const watchedReceived = Form.useWatch('amountReceived', form) ?? 0;

  // Auto-pick the bank/cash account once banks load and whenever the method
  // changes — avoids a click for the default case (cash, always-unique drawer).
  useEffect(() => {
    if (!watchedMethod || banks.length === 0) return;
    const current = form.getFieldValue('bankAccountId');
    if (current) {
      // If the current pick is no longer compatible with the chosen method, clear it.
      const ok = banks.some(
        (b) => b.id === current && (watchedMethod === 'CASH' ? b.cash : !b.cash),
      );
      if (ok) return;
    }
    form.setFieldValue('bankAccountId', pickDefaultBank(watchedMethod));
  }, [watchedMethod, banks.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const allocated = Object.values(paying).reduce(
    (s, v) => s + (Number(v) || 0),
    0,
  );
  const advanceKept = Math.max(0, Number(watchedReceived) - allocated);
  const overAllocated = allocated > Number(watchedReceived) + 0.001;
  const paidInvoiceCount = Object.values(paying).filter((v) => Number(v) > 0).length;

  const runAutoAllocate = (amount: number, reset: boolean) => {
    if (!reset && touchedRows.size > 0) {
      const ok = window.confirm(
        `Auto-allocate will overwrite ${touchedRows.size} manually-edited row${touchedRows.size === 1 ? '' : 's'}. Continue?`,
      );
      if (!ok) return;
    }
    setPaying(fifoAllocate(Number(amount) || 0, openInvoices));
    setTouchedRows(new Set());
  };

  const payAll = () => {
    form.setFieldValue('amountReceived', Math.round(totalOutstanding * 100) / 100);
    setPaying(Object.fromEntries(openInvoices.map((i) => [i.id, Number(i.dueAmount)])));
    setTouchedRows(new Set());
  };

  const payOneMonth = (groupKey: string) => {
    const group = groups.find((g) => g.key === groupKey);
    if (!group) return;
    const sel: Record<number, number> = Object.fromEntries(openInvoices.map((i) => [i.id, 0]));
    for (const i of group.invoices) sel[i.id] = Number(i.dueAmount);
    setPaying(sel);
    form.setFieldValue('amountReceived', group.total);
    setTouchedRows(new Set());
  };

  const applyAdvanceFirst = () => {
    if (advanceBalance <= 0) return;
    form.setFieldValue('amountReceived', advanceBalance);
    setPaying(fifoAllocate(advanceBalance, openInvoices));
    setTouchedRows(new Set());
  };

  const clearAllocation = () => {
    setPaying(Object.fromEntries(openInvoices.map((i) => [i.id, 0])));
    setTouchedRows(new Set());
  };

  const onFinish = (values: CollectFormShape) => {
    const lines = Object.entries(paying)
      .filter(([, v]) => Number(v) > 0)
      .map(([id, v]) => ({ invoiceId: Number(id), amountPaid: Number(v).toFixed(2) }));
    const sumLines = lines.reduce((acc, l) => acc + Number(l.amountPaid), 0);
    const received = Number(values.amountReceived) || 0;
    if (received <= 0) {
      message.error('Enter the amount received from the parent.');
      return;
    }
    if (sumLines > received + 0.001) {
      message.error('Allocated amount cannot exceed amount received.');
      return;
    }
    if (!values.bankAccountId) {
      message.error('Pick the cash drawer or bank account that received the payment.');
      return;
    }
    const adv = Math.max(0, received - sumLines);

    if (values.paymentMethod !== 'CASH') {
      localStorage.setItem(lastBankKey(values.paymentMethod), String(values.bankAccountId));
    }

    collect.mutate({
      enrollmentId: ledger.enrollmentId,
      paymentDate: values.paymentDate.format('YYYY-MM-DD'),
      paymentMethod: values.paymentMethod,
      bankAccountId: values.bankAccountId,
      proofReference: values.proofReference?.trim() || undefined,
      totalAmount: received.toFixed(2),
      advanceAmount: adv.toFixed(2),
      lines,
    });
  };

  const recentReceipts = ledger.receipts.slice(0, 5);
  const photoUrl = student?.photoPath ? fileUrl(student.photoPath) : null;

  const phoneDigits = (primaryGuardian?.phone ?? '').replace(/[^0-9]/g, '');
  const waLink = phoneDigits ? `https://wa.me/${phoneDigits}` : null;

  const copyPhone = () => {
    if (primaryGuardian?.phone) {
      navigator.clipboard.writeText(primaryGuardian.phone).then(
        () => message.success('Phone copied'),
        () => message.error('Copy failed'),
      );
    }
  };

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow={`Module / Fee · Cashier · ${ledger.registrationNumber}`}
        title={`${ledger.firstName} ${ledger.lastName}`}
        description={
          <Space size={6} wrap>
            {ledger.className && <Tag>{ledger.className} · {ledger.sectionName}</Tag>}
            <Typography.Text type="secondary">Enrollment #{ledger.enrollmentId}</Typography.Text>
          </Space>
        }
        backTo="/fee/collect"
        backLabel="Back to search"
        onRefresh={onRefresh}
        refreshKey={['fee', 'ledger', ledger.enrollmentId]}
        updatedAt={updatedAt}
        actions={
          <Space>
            <VoucherDropdown
              enrollmentId={ledger.enrollmentId}
              outstandingMonths={
                new Set(
                  openInvoices
                    .map((i) => i.periodMonth?.slice(0, 7))
                    .filter((m): m is string => !!m),
                )
              }
              onError={(msg) => message.error(msg)}
            />
            <Button icon={<FileTextOutlined />} onClick={() => navigate(`/fee/student/${ledger.enrollmentId}`)}>
              Full ledger
            </Button>
          </Space>
        }
      />

      {justCreated && (
        <Alert
          type="success"
          showIcon
          closable
          onClose={() => setJustCreated(null)}
          title={`Receipt #${justCreated.receiptNumber} created · PKR ${PKR(justCreated.totalAmount)} · ${METHOD_LABEL[justCreated.paymentMethod]}`}
          description="Hand the printable copy to the parent."
          action={
            <Space>
              <Button
                type="primary"
                icon={<PrinterOutlined />}
                onClick={() => window.open(`/fee/receipts/${justCreated.id}/print`, '_blank', 'noopener')}
              >
                Print receipt
              </Button>
              <Button onClick={() => navigate('/fee/collect')}>
                Next student
              </Button>
            </Space>
          }
        />
      )}

      <Row gutter={[16, 16]}>
        {/* Student card */}
        <Col xs={24} md={8}>
          <Card style={{ height: '100%' }}>
            <Space align="start" size={14}>
              <Avatar
                size={56}
                src={photoUrl ?? undefined}
                icon={!photoUrl ? <UserOutlined /> : undefined}
              />
              <div>
                <Typography.Text strong style={{ fontSize: 16 }}>
                  {ledger.firstName} {ledger.lastName}
                </Typography.Text>
                <div style={{ fontSize: 12, color: token.colorTextSecondary }}>
                  {ledger.className} · {ledger.sectionName} · {ledger.registrationNumber}
                </div>
              </div>
            </Space>
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${token.colorBorderSecondary}` }}>
              {primaryGuardian ? (
                <Space orientation="vertical" size={2}>
                  <Typography.Text>{primaryGuardian.fullName}</Typography.Text>
                  {primaryGuardian.phone ? (
                    <Space size={6}>
                      <PhoneOutlined style={{ fontSize: 12, color: token.colorTextTertiary }} />
                      <a href={`tel:${primaryGuardian.phone}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {primaryGuardian.phone}
                      </a>
                      <Tooltip title="Copy">
                        <Button size="small" type="text" icon={<CopyOutlined />} onClick={copyPhone} />
                      </Tooltip>
                      {waLink && (
                        <Tooltip title="WhatsApp">
                          <Button
                            size="small"
                            type="text"
                            icon={<WhatsAppOutlined />}
                            onClick={() => window.open(waLink, '_blank', 'noopener')}
                          />
                        </Tooltip>
                      )}
                    </Space>
                  ) : (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      No phone on file
                    </Typography.Text>
                  )}
                </Space>
              ) : (
                <Typography.Text type="secondary">No guardian on file</Typography.Text>
              )}
            </div>
          </Card>
        </Col>

        {/* Outstanding card */}
        <Col xs={24} md={8}>
          <Card style={{ height: '100%' }}>
            <Typography.Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Outstanding
            </Typography.Text>
            <div
              style={{
                fontSize: 30,
                fontWeight: 700,
                color: totalOutstanding > 0 ? '#dc2626' : '#16a34a',
                marginTop: 4,
              }}
            >
              PKR {PKR(totalOutstanding)}
            </div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {openInvoices.length} unpaid invoice{openInvoices.length === 1 ? '' : 's'}
              {groups.length > 0 && ` across ${groups.length} period${groups.length === 1 ? '' : 's'}`}
            </Typography.Text>
            {groups.length > 0 && (
              <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {groups.map((g) => (
                  <Tooltip
                    key={g.key}
                    title={g.invoices.map((i) => `${i.feeTypeName}: PKR ${PKR(i.dueAmount)}`).join(' · ')}
                  >
                    <Tag
                      style={{ cursor: canCreate ? 'pointer' : 'default', fontSize: 12 }}
                      onClick={canCreate ? () => payOneMonth(g.key) : undefined}
                    >
                      {g.label} · Rs {PKR(g.total)}
                    </Tag>
                  </Tooltip>
                ))}
              </div>
            )}
          </Card>
        </Col>

        {/* Advance balance card */}
        <Col xs={24} md={8}>
          <Card style={{ height: '100%' }}>
            <Typography.Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Advance balance
            </Typography.Text>
            <div
              style={{
                fontSize: 30,
                fontWeight: 700,
                color: advanceBalance > 0 ? '#1677ff' : token.colorTextSecondary,
                marginTop: 4,
              }}
            >
              PKR {PKR(advanceBalance)}
            </div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Held on account from prior overpayments.
            </Typography.Text>
            {advanceBalance > 0 && canCreate && totalOutstanding > 0 && (
              <Button
                type="link"
                style={{ padding: 0, marginTop: 8 }}
                onClick={applyAdvanceFirst}
              >
                Apply Rs {PKR(advanceBalance)} to outstanding →
              </Button>
            )}
          </Card>
        </Col>
      </Row>

      {/* Collect form (inline, no modal) */}
      {canCreate && (
        <Card
          title={
            <Space>
              <DollarOutlined />
              <span>Collect payment</span>
            </Space>
          }
          extra={
            openInvoices.length > 0 && (
              <Button size="small" type="text" onClick={() => setCustomMode((m) => !m)}>
                {customMode ? 'Hide allocation table' : 'Customize allocation'}
              </Button>
            )
          }
        >
          {noBanks && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              title="No cash drawers or bank accounts configured"
              description="Add one under Finance → Bank Accounts before recording a receipt."
            />
          )}

          <Form
            form={form}
            layout="vertical"
            initialValues={{
              paymentDate: dayjs(),
              paymentMethod: 'CASH' as PaymentMethod,
            }}
            onValuesChange={(changed) => {
              // Auto-FIFO allocate the moment the cashier types the amount received,
              // but only if they haven't manually edited rows yet.
              if ('amountReceived' in changed && touchedRows.size === 0 && openInvoices.length > 0) {
                const n = Number(changed.amountReceived) || 0;
                setPaying(fifoAllocate(n, openInvoices));
              }
            }}
            onFinish={onFinish}
          >
            <Row gutter={12}>
              <Col xs={24} md={6}>
                <Form.Item
                  label="Amount received from parent"
                  name="amountReceived"
                  rules={[{ required: true, type: 'number', min: 0.01, message: 'Required' }]}
                >
                  <InputNumber
                    ref={amountRef}
                    min={0}
                    step={500}
                    style={{ width: '100%' }}
                    prefix="Rs"
                    autoFocus
                    placeholder="0"
                    onPressEnter={() => form.submit()}
                  />
                </Form.Item>
              </Col>
              <Col xs={12} md={4}>
                <Form.Item label="Payment date" name="paymentDate" rules={[{ required: true }]}>
                  <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
                </Form.Item>
              </Col>
              <Col xs={12} md={4}>
                <Form.Item label="Method" name="paymentMethod" rules={[{ required: true }]}>
                  <Select
                    options={(Object.keys(METHOD_LABEL) as PaymentMethod[]).map((m) => ({
                      value: m,
                      label: METHOD_LABEL[m],
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={5}>
                <Form.Item
                  label={watchedMethod === 'CASH' ? 'Cash drawer' : 'Bank account'}
                  name="bankAccountId"
                  rules={[{ required: true, message: 'Required — drives GL posting' }]}
                >
                  <Select
                    loading={banksQuery.isLoading}
                    placeholder={banks.length === 0 ? 'No accounts configured' : '—'}
                    options={banks
                      .filter((b) => (watchedMethod === 'CASH' ? b.cash : !b.cash))
                      .map((b) => ({
                        value: b.id,
                        label: `${b.accountName}${b.bankName ? ` · ${b.bankName}` : ''}`,
                      }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={5}>
                <Form.Item
                  label={watchedMethod === 'CASH' ? 'Reference (optional)' : 'Proof reference'}
                  name="proofReference"
                  rules={[
                    { required: watchedMethod !== 'CASH', message: 'Required for non-cash', max: 200 },
                  ]}
                >
                  <Input placeholder={watchedMethod === 'CASH' ? 'Slip / note (optional)' : 'TX#1234567 or slip #'} />
                </Form.Item>
              </Col>
            </Row>

            {/* Quick allocation actions */}
            {openInvoices.length > 0 && (
              <Space wrap style={{ marginBottom: 8 }}>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>Quick allocate:</Typography.Text>
                <Button
                  size="small"
                  onClick={() => runAutoAllocate(Number(watchedReceived) || 0, false)}
                  disabled={Number(watchedReceived) <= 0}
                >
                  Oldest first (FIFO)
                </Button>
                <Button size="small" onClick={payAll}>
                  Pay everything (Rs {PKR(totalOutstanding)})
                </Button>
                {advanceBalance > 0 && (
                  <Button size="small" onClick={applyAdvanceFirst}>
                    Use advance balance (Rs {PKR(advanceBalance)})
                  </Button>
                )}
                <Button size="small" type="text" onClick={clearAllocation}>
                  Clear
                </Button>
              </Space>
            )}

            {/* Allocation preview / table */}
            {openInvoices.length === 0 ? (
              <Alert
                type="info"
                showIcon
                title="No outstanding invoices"
                description="This payment will be kept entirely as advance balance on the student's account."
              />
            ) : !customMode ? (
              <AllocationSummary groups={groups} paying={paying} touched={touchedRows} />
            ) : (
              <AllocationTable
                groups={groups}
                onChangeRow={(id, val) => {
                  setPaying((p) => ({ ...p, [id]: val }));
                  setTouchedRows((t) => new Set(t).add(id));
                }}
                paying={paying}
                touched={touchedRows}
              />
            )}

            <Alert
              type={overAllocated ? 'error' : 'info'}
              showIcon
              style={{ marginTop: 12 }}
              title={
                overAllocated
                  ? `Allocated PKR ${PKR(allocated)} exceeds received PKR ${PKR(Number(watchedReceived))}. Reduce a line.`
                  : `Received PKR ${PKR(Number(watchedReceived))} · Pay ${paidInvoiceCount} invoice${paidInvoiceCount === 1 ? '' : 's'} (PKR ${PKR(allocated)}) · Advance kept: PKR ${PKR(advanceKept)}`
              }
            />

            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button
                type="primary"
                size="large"
                icon={<DollarOutlined />}
                onClick={() => form.submit()}
                loading={collect.isPending}
                disabled={noBanks || overAllocated}
              >
                Record receipt
              </Button>
            </div>
          </Form>
        </Card>
      )}

      {/* Recent receipts (compact) */}
      <Card
        title={
          <Space>
            <FileTextOutlined />
            <span>Recent receipts</span>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              (last {recentReceipts.length})
            </Typography.Text>
          </Space>
        }
        extra={
          ledger.receipts.length > recentReceipts.length && (
            <Button
              type="link"
              size="small"
              onClick={() => navigate(`/fee/student/${ledger.enrollmentId}`)}
            >
              View all ({ledger.receipts.length})
            </Button>
          )
        }
      >
        {recentReceipts.length === 0 ? (
          <Empty description="No receipts yet" />
        ) : (
          <Space orientation="vertical" size={0} style={{ width: '100%' }}>
            {recentReceipts.map((r) => (
              <div
                key={r.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 0',
                  borderBottom: `1px solid ${token.colorBorderSecondary}`,
                  opacity: r.reversed ? 0.6 : 1,
                }}
              >
                <Typography.Text code style={{ minWidth: 60, textDecoration: r.reversed ? 'line-through' : undefined }}>
                  #{r.receiptNumber}
                </Typography.Text>
                <Typography.Text type="secondary" style={{ minWidth: 110, fontSize: 13 }}>
                  {dayjs(r.paymentDate).format('D MMM YYYY')}
                </Typography.Text>
                <Tag>{METHOD_LABEL[r.paymentMethod]}</Tag>
                <Typography.Text strong style={{ minWidth: 100, textDecoration: r.reversed ? 'line-through' : undefined }}>
                  PKR {PKR(r.totalAmount)}
                </Typography.Text>
                {Number(r.advanceAmount) > 0 && r.lines.length === 0 && (
                  <Tag color="blue">Advance only</Tag>
                )}
                {r.reversed && <Tag>Reversed</Tag>}
                <div style={{ flex: 1 }} />
                <Tooltip title="Print">
                  <Button
                    size="small"
                    icon={<PrinterOutlined />}
                    onClick={() => window.open(`/fee/receipts/${r.id}/print`, '_blank', 'noopener')}
                  />
                </Tooltip>
                {canViewHistory && (
                  <Tooltip title="Audit history">
                    <Button
                      size="small"
                      icon={<HistoryOutlined />}
                      onClick={() => setHistoryReceiptId(r.id)}
                    />
                  </Tooltip>
                )}
              </div>
            ))}
          </Space>
        )}
      </Card>

      <AuditHistoryDrawer
        entityType="fee-receipt"
        entityId={historyReceiptId}
        open={historyReceiptId != null}
        onClose={() => setHistoryReceiptId(null)}
      />
    </div>
  );
}

/** Compact summary view of how the current `paying` map allocates across month groups. */
function AllocationSummary({
  groups,
  paying,
  touched,
}: {
  groups: { key: string; label: string; invoices: Invoice[]; total: number }[];
  paying: Record<number, number>;
  touched: Set<number>;
}) {
  const { token } = antdTheme.useToken();
  return (
    <div
      style={{
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: 8,
        padding: 12,
        background: token.colorFillQuaternary,
      }}
    >
      {groups.map((g) => {
        const groupPaying = g.invoices.reduce((s, i) => s + (Number(paying[i.id]) || 0), 0);
        const pct = g.total > 0 ? Math.round((groupPaying / g.total) * 100) : 0;
        if (groupPaying === 0) return null;
        return (
          <div key={g.key} style={{ marginBottom: 6 }}>
            <Space size={8}>
              <Typography.Text strong style={{ fontSize: 13 }}>
                {g.label}
              </Typography.Text>
              <Typography.Text>
                Rs {Number(groupPaying).toLocaleString('en-PK')} / Rs {Number(g.total).toLocaleString('en-PK')}
              </Typography.Text>
              <Tag color={pct === 100 ? 'success' : 'warning'}>{pct}%</Tag>
            </Space>
            <div style={{ fontSize: 12, color: token.colorTextSecondary, marginLeft: 4 }}>
              {g.invoices
                .filter((i) => (Number(paying[i.id]) || 0) > 0)
                .map(
                  (i) =>
                    `${i.feeTypeName} Rs ${Number(paying[i.id]).toLocaleString('en-PK')}${touched.has(i.id) ? ' (manual)' : ''}`,
                )
                .join(' · ') || '—'}
            </div>
          </div>
        );
      })}
      {!groups.some((g) => g.invoices.some((i) => (Number(paying[i.id]) || 0) > 0)) && (
        <Typography.Text type="secondary">
          Nothing allocated yet — enter an amount above or click a quick action.
        </Typography.Text>
      )}
    </div>
  );
}

/** Per-invoice editor — shown when "Customize allocation" is on. */
function AllocationTable({
  groups,
  paying,
  touched,
  onChangeRow,
}: {
  groups: { key: string; label: string; invoices: Invoice[]; total: number }[];
  paying: Record<number, number>;
  touched: Set<number>;
  onChangeRow: (id: number, value: number) => void;
}) {
  const { token } = antdTheme.useToken();
  return (
    <div style={{ border: `1px solid ${token.colorBorderSecondary}`, borderRadius: 8 }}>
      {groups.map((g) => (
        <div key={g.key} style={{ borderBottom: `1px solid ${token.colorBorderSecondary}` }}>
          <div
            style={{
              padding: '8px 12px',
              background: token.colorFillQuaternary,
              fontSize: 12,
              fontWeight: 600,
              color: token.colorTextSecondary,
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <span>{g.label}</span>
            <span>{g.invoices.length} fees · Rs {Number(g.total).toLocaleString('en-PK')}</span>
          </div>
          {g.invoices.map((i) => (
            <div
              key={i.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '8px 12px',
                borderTop: `1px solid ${token.colorBorderSecondary}`,
              }}
            >
              <Typography.Text style={{ flex: 1 }}>
                {i.feeTypeName}{i.manual ? <Tag color="orange" style={{ marginLeft: 6 }}>Manual</Tag> : null}
              </Typography.Text>
              <Typography.Text type="secondary" style={{ minWidth: 100, textAlign: 'right', fontSize: 13 }}>
                Due Rs {Number(i.dueAmount).toLocaleString('en-PK')}
              </Typography.Text>
              <InputNumber
                min={0}
                max={Number(i.dueAmount)}
                step={100}
                style={{ width: 140 }}
                value={paying[i.id] ?? 0}
                onChange={(v) => onChangeRow(i.id, Number(v) || 0)}
                placeholder="0"
                status={touched.has(i.id) ? 'warning' : undefined}
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/* ---------- Voucher dropdown ---------- */

function VoucherDropdown({
  enrollmentId,
  outstandingMonths,
  onError,
}: {
  enrollmentId: number;
  outstandingMonths: Set<string>;
  onError: (msg: string) => void;
}) {
  const now = dayjs();
  const offsets = [1, 0, -1, -2, -3];
  const months = offsets.map((delta) => now.add(delta, 'month'));
  const hasDue = (m: Dayjs) => outstandingMonths.has(m.format('YYYY-MM'));
  const nowHasDue = hasDue(now);
  // Primary click target: current month if it has due, otherwise the nearest
  // listed month that does. Falls back to `now` when nothing is due anywhere
  // (button will be disabled in that state).
  const primary = nowHasDue ? now : months.find(hasDue) ?? now;
  const allPaid = outstandingMonths.size === 0;

  const downloadVoucher = async (month: Dayjs) => {
    const m = month.format('YYYY-MM');
    try {
      await downloadFile(
        `/reports/exports/fee-voucher/${enrollmentId}.pdf`,
        { month: m },
        `fee-voucher-e${enrollmentId}-${m}.pdf`,
      );
    } catch (e) {
      onError(extractError(e, 'Voucher download failed'));
    }
  };

  return (
    <Tooltip title={allPaid ? 'No outstanding fees — nothing to print on a voucher' : !nowHasDue ? `Nothing due for ${now.format('MMM YYYY')} — clicking prints ${primary.format('MMM YYYY')} instead` : undefined}>
      <Dropdown.Button
        icon={<PrinterOutlined />}
        onClick={() => downloadVoucher(primary)}
        disabled={allPaid}
        menu={{
          items: months.map((m) => {
            const due = hasDue(m);
            return {
              key: m.format('YYYY-MM'),
              disabled: !due,
              label: (
                <Space size={6}>
                  <span>
                    {m.format('MMM YYYY')}
                    {m.isSame(now, 'month') ? ' · this month' : ''}
                  </span>
                  {!due && <Tag style={{ marginInlineEnd: 0 }}>Paid</Tag>}
                </Space>
              ),
            };
          }),
          onClick: ({ key }) => {
            if (!outstandingMonths.has(key)) return;
            downloadVoucher(dayjs(key + '-01'));
          },
        }}
      >
        Print voucher
      </Dropdown.Button>
    </Tooltip>
  );
}
